import { el, onTeardown } from '../lib/dom.js';
import { createPinball } from '../lib/pinball.js';

/**
 * TEMPORARY dev tool — a drag-to-edit layout board for the pinball table
 * (#/pinball). Lets a human push points/circles around visually instead of
 * hand-computing coordinates, then exports the result as JSON so the real
 * geometry in src/lib/pinball.js can be rebuilt from it. Not linked from
 * anywhere but the hidden Pinball view's nav row; delete this file (and its
 * route + nav link) once the table layout is settled.
 */

const W = 320;
const H = 560;
const SCALE = 1.6; // canvas is drawn bigger than the real game for precision
const STORE_KEY = 'pdx.pinballEditorLayout';

const CX = 160; // matches the live table's actual centre (playfield + lane)
const mx = (x) => 2 * CX - x;

function arcPts(cx, cy, r, a0, a1, n) {
  const p = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    p.push([+(cx + Math.cos(a) * r).toFixed(1), +(cy + Math.sin(a) * r).toFixed(1)]);
  }
  return p;
}
const D = Math.PI / 180;

// The geometry actually shipped in src/lib/pinball.js right now — this is
// what "Reset to defaults" goes back to. Keep this in sync by hand when the
// live TABLE changes (there's no automatic link between the two files).
function defaultLayout() {
  return {
    bound: [
      [70, 550],
      [22, 520],
      [8, 446],
      [8, 50],
      ...arcPts(48, 50, 40, 180 * D, 270 * D, 8),
      [261, 10],
      [270, 11],
      [278, 13],
      [285, 17],
      [292, 23],
      [298, 30],
      [303, 37],
      [304, 45],
      [305, 54],
      [305, 451],
      [307, 535],
      [267, 552],
    ],
    outlaneL: [
      [54, 405],
      [54, 445],
      [57, 500],
      [102, 522],
    ],
    outlaneR: [
      [266, 405],
      [266, 445],
      [263, 500],
      [218, 522],
    ],
    laneOuter: [
      [306, 505],
      [304, 139],
    ],
    laneFloor: [
      [306, 504],
      [288, 504],
    ],
    laneInner: [
      [288, 506],
      [288, 151],
      [287, 145],
      [285, 139],
      [281, 134],
      [276, 130],
      [271, 127],
    ],
    slingL: [
      [71, 422],
      [139, 457],
      [96, 456],
    ], // apex, kick-face base, other base
    slingR: [
      [249, 422],
      [181, 457],
      [224, 456],
    ],
    bumpers: [
      { x: 114, y: 114, r: 15 },
      { x: 160, y: 72, r: 16 },
      { x: 206, y: 114, r: 15 },
      { x: 136, y: 182, r: 12 },
      { x: 184, y: 182, r: 12 },
    ],
    posts: [
      { x: 64, y: 283, r: 7 },
      { x: 256, y: 283, r: 7 },
      { x: 160, y: 279, r: 8 },
    ],
    flipperL: { pivot: [96, 524], tip: [96 + 56 * Math.cos(0.28), 524 + 56 * Math.sin(0.28)] },
    flipperR: {
      pivot: [224, 524],
      tip: [224 + 56 * Math.cos(Math.PI - 0.28), 524 + 56 * Math.sin(Math.PI - 0.28)],
    },
    laneX: 295,
    laneRestY: 488,
    drainY: 546,
  };
}

function loadLayout() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* corrupt or unavailable — fall through to defaults */
  }
  return defaultLayout();
}

const GROUP_COLOR = {
  bound: '#eef0f2',
  outlaneL: '#54e0a6',
  outlaneR: '#54e0a6',
  laneOuter: '#7fb2ff',
  laneFloor: '#7fb2ff',
  laneInner: '#7fb2ff',
  slingL: '#ff6a5c',
  slingR: '#ff6a5c',
  bumpers: '#ffd23f',
  posts: '#c084fc',
  flipperL: '#f7c948',
  flipperR: '#f7c948',
};
const POLYLINES = [
  'bound',
  'outlaneL',
  'outlaneR',
  'laneOuter',
  'laneFloor',
  'laneInner',
  'slingL',
  'slingR',
];
const MIRROR_PAIRS = [
  ['outlaneL', 'outlaneR'],
  ['slingL', 'slingR'],
];

// Keep in step with PHYS.slingE / PHYS.slingKick in pinball.js — the editor
// doesn't import PHYS (it's not exported), so this is a best-effort copy
// just for the "play test" preview's feel, not the source of truth.
const SLING_E = 1.3;
const SLING_KICK = 230;

/** A flat editor layout → the {outline, bound, walls, bumpers, posts,
 * flippers, laneX, laneRestY, drainY, returnMinX} shape createPinball()
 * expects, for the editor's own "play test" mode. */
function layoutToTable(layout) {
  const polySegs = (pts, extra) => {
    const s = [];
    for (let i = 0; i < pts.length - 1; i++) s.push({ a: pts[i], b: pts[i + 1], ...extra });
    return s;
  };
  const flipperFrom = (f) => {
    const dx = f.tip[0] - f.pivot[0];
    const dy = f.tip[1] - f.pivot[1];
    return { pivot: [...f.pivot], rest: Math.atan2(dy, dx), len: Math.hypot(dx, dy) };
  };
  const outline = [
    ...arcPts(18, 18, 16, 180 * D, 270 * D, 6),
    ...arcPts(302, 18, 16, 270 * D, 360 * D, 6),
    ...arcPts(302, 542, 16, 0, 90 * D, 6),
    ...arcPts(18, 542, 16, 90 * D, 180 * D, 6),
  ];
  return {
    outline,
    bound: layout.bound,
    outlaneL: layout.outlaneL,
    outlaneR: layout.outlaneR,
    laneOuter: layout.laneOuter,
    laneFloor: layout.laneFloor,
    laneInner: layout.laneInner,
    walls: [
      ...polySegs(layout.bound, { role: 'wall' }),
      ...polySegs(layout.outlaneL, { role: 'guide' }),
      ...polySegs(layout.outlaneR, { role: 'guide' }),
      ...polySegs(layout.laneOuter, { role: 'lane' }),
      ...polySegs(layout.laneFloor, { role: 'lane' }),
      ...polySegs(layout.laneInner, { role: 'lane' }),
      { a: layout.slingL[0], b: layout.slingL[1], e: SLING_E, k: SLING_KICK, role: 'sling' },
      { a: layout.slingL[0], b: layout.slingL[2], role: 'sling' },
      { a: layout.slingL[2], b: layout.slingL[1], role: 'sling' },
      { a: layout.slingR[0], b: layout.slingR[1], e: SLING_E, k: SLING_KICK, role: 'sling' },
      { a: layout.slingR[0], b: layout.slingR[2], role: 'sling' },
      { a: layout.slingR[2], b: layout.slingR[1], role: 'sling' },
    ],
    bumpers: layout.bumpers.map((b) => ({ ...b, kind: 'poke' })),
    posts: layout.posts.map((p) => ({ ...p })),
    flippers: { L: flipperFrom(layout.flipperL), R: flipperFrom(layout.flipperR) },
    laneX: layout.laneX,
    laneRestY: layout.laneRestY,
    drainY: layout.drainY,
    returnMinX: 999,
  };
}

export async function PinballEditorView() {
  const view = el('section', { class: 'view pin-ed' });
  let layout = loadLayout();
  let mirrorLock = true;
  let drag = null; // { kind:'point'|'circle'|'move'|'multiMove'|'marquee'|'flipperPivot'|'flipperTip'|'plunger', ... }
  let selected = new Set(); // handle keys, "group:index" — points + circles only
  let undoStack = [];
  function pushUndo() {
    undoStack.push(JSON.stringify(layout));
    if (undoStack.length > 50) undoStack.shift();
  }
  function undo() {
    if (!undoStack.length) {
      status.textContent = 'Nothing to undo.';
      return;
    }
    layout = JSON.parse(undoStack.pop());
    selected = new Set();
    render();
    save();
    updateSelStatus();
    status.textContent = 'Undid last change.';
  }

  const keyOf = (hit) => `${hit.group}:${hit.index}`;
  function handlePos(key) {
    const [group, idxStr] = key.split(':');
    const i = Number(idxStr);
    return POLYLINES.includes(group) ? layout[group][i] : [layout[group][i].x, layout[group][i].y];
  }
  function setHandlePos(key, x, y) {
    const [group, idxStr] = key.split(':');
    const i = Number(idxStr);
    if (POLYLINES.includes(group)) layout[group][i] = [x, y];
    else {
      layout[group][i].x = x;
      layout[group][i].y = y;
    }
  }

  const canvas = el('canvas', {
    width: Math.round(W * SCALE),
    height: Math.round(H * SCALE),
    style: `width:${W * SCALE}px;height:${H * SCALE}px;touch-action:none;background:#0d0e12;border-radius:8px;cursor:crosshair;`,
  });
  const ctx = canvas.getContext('2d');
  const jsonBox = el('textarea', {
    class: 'pin-ed__json',
    readOnly: true,
    rows: 14,
    spellcheck: false,
  });
  const status = el('span', { class: 'pin-ed__status' }, '');
  const selCount = el('span', { class: 'pin-ed__selcount' }, '');
  function updateSelStatus() {
    selCount.textContent = selected.size
      ? `${selected.size} selected — drag to move together, Delete to remove`
      : '';
  }

  function save() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(layout));
    } catch {
      /* storage unavailable — the export box still has the data */
    }
    jsonBox.value = JSON.stringify(layout, null, 2);
  }

  function toCanvas([x, y]) {
    return [x * SCALE, y * SCALE];
  }
  function toLogical(cx, cy) {
    return [cx / SCALE, cy / SCALE];
  }

  function drawPolyline(pts, color, close = false) {
    ctx.beginPath();
    pts.forEach(([x, y], i) => {
      const [cx, cy] = toCanvas([x, y]);
      i === 0 ? ctx.moveTo(cx, cy) : ctx.lineTo(cx, cy);
    });
    if (close) ctx.closePath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  function drawHandle([x, y], color, r = 5, isSelected = false) {
    const [cx, cy] = toCanvas([x, y]);
    if (isSelected) {
      ctx.beginPath();
      ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 2]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = isSelected ? '#fff' : '#000';
    ctx.stroke();
  }

  function render() {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // faint floor + grid for scale reference
    ctx.fillStyle = '#d8b878';
    ctx.fillRect(...toCanvas([12, 14]), ...[296 * SCALE, (H - 20) * SCALE]);
    ctx.strokeStyle = 'rgba(0,0,0,0.08)';
    ctx.lineWidth = 1;
    for (let gx = 0; gx <= W; gx += 20)
      drawPolyline(
        [
          [gx, 0],
          [gx, H],
        ],
        'rgba(0,0,0,0.08)',
      );
    for (let gy = 0; gy <= H; gy += 20)
      drawPolyline(
        [
          [0, gy],
          [W, gy],
        ],
        'rgba(0,0,0,0.08)',
      );

    for (const g of POLYLINES) {
      drawPolyline(layout[g], GROUP_COLOR[g]);
      layout[g].forEach((p, i) => drawHandle(p, GROUP_COLOR[g], 5, selected.has(`${g}:${i}`)));
    }
    for (const g of ['bumpers', 'posts']) {
      layout[g].forEach((c, i) => {
        const [cx, cy] = toCanvas([c.x, c.y]);
        ctx.beginPath();
        ctx.arc(cx, cy, c.r * SCALE, 0, Math.PI * 2);
        ctx.strokeStyle = GROUP_COLOR[g];
        ctx.lineWidth = 2;
        ctx.stroke();
        drawHandle([c.x, c.y], GROUP_COLOR[g], 4, selected.has(`${g}:${i}`));
      });
    }
    for (const g of ['flipperL', 'flipperR']) {
      const f = layout[g];
      drawPolyline([f.pivot, f.tip], GROUP_COLOR[g]);
      drawHandle(f.pivot, GROUP_COLOR[g], 6);
      drawHandle(f.tip, '#ffe08a', 5);
    }
    // plunger — where the ball rests in the lane before launch
    drawHandle([layout.laneX, layout.laneRestY], '#ffffff', 6, drag?.kind === 'plunger');
    // centre line, for eyeballing symmetry
    drawPolyline(
      [
        [CX, 0],
        [CX, H],
      ],
      'rgba(255,255,255,0.25)',
    );

    if (drag?.kind === 'marquee') {
      const [x0, y0] = toCanvas(drag.start);
      const [x1, y1] = toCanvas(drag.cur);
      ctx.fillStyle = 'rgba(127,178,255,0.15)';
      ctx.fillRect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
      ctx.strokeStyle = 'rgba(127,178,255,0.8)';
      ctx.lineWidth = 1;
      ctx.strokeRect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
    }
  }

  function mirrorPartnerGroup(group) {
    for (const [l, r] of MIRROR_PAIRS) {
      if (group === l) return r;
      if (group === r) return l;
    }
    return null;
  }

  function hitTest(lx, ly) {
    const HIT = 10 / SCALE;
    for (const g of POLYLINES) {
      for (let i = 0; i < layout[g].length; i++) {
        const [px, py] = layout[g][i];
        if (Math.hypot(px - lx, py - ly) < HIT) return { kind: 'point', group: g, index: i };
      }
    }
    for (const g of ['bumpers', 'posts']) {
      for (let i = 0; i < layout[g].length; i++) {
        const c = layout[g][i];
        if (Math.hypot(c.x - lx, c.y - ly) < HIT) return { kind: 'circle', group: g, index: i };
      }
    }
    for (const g of ['flipperL', 'flipperR']) {
      const f = layout[g];
      if (Math.hypot(f.pivot[0] - lx, f.pivot[1] - ly) < HIT)
        return { kind: 'flipperPivot', group: g };
      if (Math.hypot(f.tip[0] - lx, f.tip[1] - ly) < HIT) return { kind: 'flipperTip', group: g };
    }
    if (Math.hypot(layout.laneX - lx, layout.laneRestY - ly) < HIT) return { kind: 'plunger' };
    return null;
  }

  function pointerLogical(e) {
    const rect = canvas.getBoundingClientRect();
    return toLogical(
      ((e.clientX - rect.left) / rect.width) * canvas.width,
      ((e.clientY - rect.top) / rect.height) * canvas.height,
    );
  }

  /** Closest point on segment a→b to p (logical units). */
  function closestOnSeg(px, py, [ax, ay], [bx, by]) {
    const abx = bx - ax;
    const aby = by - ay;
    const t = Math.max(
      0,
      Math.min(1, ((px - ax) * abx + (py - ay) * aby) / (abx * abx + aby * aby || 1)),
    );
    return [ax + t * abx, ay + t * aby];
  }

  /** Nearest point ON A LINE BODY (not a handle) across every polyline. */
  function hitTestLine(lx, ly) {
    const HIT = 7 / SCALE;
    let best = null;
    for (const g of POLYLINES) {
      const pts = layout[g];
      for (let i = 0; i < pts.length - 1; i++) {
        const [cx, cy] = closestOnSeg(lx, ly, pts[i], pts[i + 1]);
        const d = Math.hypot(cx - lx, cy - ly);
        if (d < HIT && (!best || d < best.d)) best = { group: g, segIndex: i, d };
      }
    }
    return best;
  }

  canvas.addEventListener('pointerdown', (e) => {
    const [lx, ly] = pointerLogical(e);
    const handle = hitTest(lx, ly);
    if (handle && (handle.kind === 'point' || handle.kind === 'circle')) {
      const key = keyOf(handle);
      if (e.shiftKey) {
        selected.has(key) ? selected.delete(key) : selected.add(key);
        drag = null; // shift-click only adjusts the selection, no drag
        render();
        updateSelStatus();
        return;
      }
      if (selected.has(key) && selected.size > 1) {
        // dragging a handle that's part of a multi-selection moves all of it
        drag = {
          kind: 'multiMove',
          start: [lx, ly],
          keys: [...selected],
          snapshot: [...selected].map((k) => handlePos(k)),
        };
      } else {
        selected = new Set([key]);
        drag = handle;
      }
    } else if (handle) {
      drag = handle; // flipper pivot/tip — not selectable, just draggable
    } else {
      const line = hitTestLine(lx, ly);
      if (line) {
        // click-drag on a line's body (not a handle) moves the WHOLE shape
        selected = new Set();
        drag = {
          kind: 'move',
          group: line.group,
          start: [lx, ly],
          snapshot: layout[line.group].map((p) => [...p]),
        };
      } else {
        // empty space — rubber-band select
        if (!e.shiftKey) selected = new Set();
        drag = { kind: 'marquee', start: [lx, ly], cur: [lx, ly], additive: e.shiftKey };
      }
    }
    if (drag && drag.kind !== 'marquee') pushUndo(); // snapshot before this drag can change anything
    if (drag) canvas.setPointerCapture(e.pointerId);
    render();
    updateSelStatus();
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!drag) return;
    let [lx, ly] = pointerLogical(e);
    lx = Math.round(Math.max(0, Math.min(W, lx)));
    ly = Math.round(Math.max(0, Math.min(H, ly)));

    if (drag.kind === 'point') {
      layout[drag.group][drag.index] = [lx, ly];
      if (mirrorLock) {
        const partner = mirrorPartnerGroup(drag.group);
        if (partner && layout[partner][drag.index]) layout[partner][drag.index] = [mx(lx), ly];
      }
    } else if (drag.kind === 'move') {
      const dx = lx - drag.start[0];
      const dy = ly - drag.start[1];
      layout[drag.group] = drag.snapshot.map(([x, y]) => [x + dx, y + dy]);
      if (mirrorLock) {
        const partner = mirrorPartnerGroup(drag.group);
        if (partner) layout[partner] = drag.snapshot.map(([x, y]) => [mx(x + dx), y + dy]);
      }
    } else if (drag.kind === 'circle') {
      layout[drag.group][drag.index].x = lx;
      layout[drag.group][drag.index].y = ly;
    } else if (drag.kind === 'flipperPivot') {
      const f = layout[drag.group];
      const dx = lx - f.pivot[0];
      const dy = ly - f.pivot[1];
      f.pivot = [lx, ly];
      f.tip = [f.tip[0] + dx, f.tip[1] + dy];
      if (mirrorLock) {
        const other = drag.group === 'flipperL' ? layout.flipperR : layout.flipperL;
        other.pivot = [mx(lx), ly];
      }
    } else if (drag.kind === 'flipperTip') {
      layout[drag.group].tip = [lx, ly];
    } else if (drag.kind === 'plunger') {
      layout.laneX = lx;
      layout.laneRestY = ly;
    } else if (drag.kind === 'multiMove') {
      const dx = lx - drag.start[0];
      const dy = ly - drag.start[1];
      drag.keys.forEach((k, i) => {
        const [ox, oy] = drag.snapshot[i];
        setHandlePos(k, ox + dx, oy + dy);
      });
    } else if (drag.kind === 'marquee') {
      drag.cur = [lx, ly];
      render();
      return; // nothing to save — no data changed yet
    }
    render();
    save();
  });
  const endDrag = () => {
    if (drag?.kind === 'marquee') {
      const [x0, y0] = drag.start;
      const [x1, y1] = drag.cur;
      const lo = [Math.min(x0, x1), Math.min(y0, y1)];
      const hi = [Math.max(x0, x1), Math.max(y0, y1)];
      const inBox = ([x, y]) => x >= lo[0] && x <= hi[0] && y >= lo[1] && y <= hi[1];
      const next = drag.additive ? selected : new Set();
      for (const g of POLYLINES) layout[g].forEach((p, i) => inBox(p) && next.add(`${g}:${i}`));
      for (const g of ['bumpers', 'posts'])
        layout[g].forEach((c, i) => inBox([c.x, c.y]) && next.add(`${g}:${i}`));
      selected = next;
      updateSelStatus();
    }
    drag = null;
    render();
  };
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  // double-click a handle to delete it; double-click a bare line to add a
  // point there (mirrored to the paired group, while lock is on)
  canvas.addEventListener('dblclick', (e) => {
    e.preventDefault();
    const [lx, ly] = pointerLogical(e);
    const handle = hitTest(lx, ly);
    if (handle) {
      if (handle.kind === 'point') {
        if (layout[handle.group].length <= 2) {
          status.textContent = 'A line needs at least 2 points — add one before deleting.';
          return;
        }
        pushUndo();
        layout[handle.group].splice(handle.index, 1);
        const partner = mirrorLock && mirrorPartnerGroup(handle.group);
        if (partner && layout[partner][handle.index]) layout[partner].splice(handle.index, 1);
        status.textContent = `Deleted a point from ${handle.group}.`;
      } else if (handle.kind === 'circle') {
        pushUndo();
        layout[handle.group].splice(handle.index, 1);
        status.textContent = `Deleted a ${handle.group.slice(0, -1)}.`;
      } else {
        status.textContent = "Flippers can't be deleted.";
        return;
      }
      selected = new Set(); // indices shifted — any prior selection is now stale
      render();
      save();
      updateSelStatus();
      return;
    }
    const line = hitTestLine(lx, ly);
    if (line) {
      pushUndo();
      const pt = [Math.round(lx), Math.round(ly)];
      layout[line.group].splice(line.segIndex + 1, 0, pt);
      const partner = mirrorLock && mirrorPartnerGroup(line.group);
      if (partner) layout[partner].splice(line.segIndex + 1, 0, [mx(pt[0]), pt[1]]);
      status.textContent = `Added a point to ${line.group}.`;
      selected = new Set();
      render();
      save();
      updateSelStatus();
    }
  });

  // scroll to resize a bumper/post
  canvas.addEventListener(
    'wheel',
    (e) => {
      const [lx, ly] = pointerLogical(e);
      const hit = hitTest(lx, ly);
      if (hit?.kind !== 'circle') return;
      e.preventDefault();
      const c = layout[hit.group][hit.index];
      c.r = Math.max(4, Math.min(40, c.r - Math.sign(e.deltaY)));
      render();
      save();
    },
    { passive: false },
  );

  function deleteSelected() {
    if (!selected.size) return;
    pushUndo();
    const byGroup = new Map();
    for (const k of selected) {
      const [g, iStr] = k.split(':');
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(Number(iStr));
    }
    let blocked = false;
    for (const [g, idxs] of byGroup) {
      idxs.sort((a, b) => b - a); // delete highest index first so earlier ones stay valid
      if (POLYLINES.includes(g) && layout[g].length - idxs.length < 2) {
        blocked = true;
        continue; // keep at least 2 points on a line
      }
      for (const i of idxs) layout[g].splice(i, 1);
    }
    selected = new Set();
    status.textContent = blocked
      ? 'Deleted what I could — a line needs at least 2 points. (This does not auto-mirror; use "Mirror L → R now" after.)'
      : 'Deleted the selection. (This does not auto-mirror; use "Mirror L → R now" after if needed.)';
    render();
    save();
    updateSelStatus();
  }
  const onKeyDown = (e) => {
    if ((e.key === 'z' || e.key === 'Z') && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      undo();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && selected.size) {
      e.preventDefault();
      deleteSelected();
    } else if (e.key === 'Escape' && selected.size) {
      selected = new Set();
      render();
      updateSelStatus();
    }
  };
  window.addEventListener('keydown', onKeyDown);

  const addCircle = (group, defaults) => {
    pushUndo();
    layout[group].push({ ...defaults });
    render();
    save();
    status.textContent = `Added a ${group.slice(0, -1)} — drag it into place.`;
  };
  const addBumperBtn = el('button', {
    class: 'button button--ghost',
    type: 'button',
    onclick: () => addCircle('bumpers', { x: CX, y: 260, r: 14 }),
  });
  addBumperBtn.textContent = '+ Bumper';
  const addPostBtn = el('button', {
    class: 'button button--ghost',
    type: 'button',
    onclick: () => addCircle('posts', { x: CX, y: 260, r: 7 }),
  });
  addPostBtn.textContent = '+ Post';

  const mirrorBtn = el('button', {
    class: 'button button--ghost',
    type: 'button',
    onclick: () => {
      mirrorLock = !mirrorLock;
      mirrorBtn.textContent = mirrorLock ? 'Mirror lock: on' : 'Mirror lock: off';
    },
  });
  mirrorBtn.textContent = 'Mirror lock: on';

  const mirrorNowBtn = el('button', {
    class: 'button button--ghost',
    type: 'button',
    onclick: () => {
      pushUndo();
      for (const [l, r] of MIRROR_PAIRS) {
        layout[r] = layout[l].map(([x, y]) => [mx(x), y]);
      }
      layout.flipperR.pivot = [mx(layout.flipperL.pivot[0]), layout.flipperL.pivot[1]];
      layout.flipperR.tip = [mx(layout.flipperL.tip[0]), layout.flipperL.tip[1]];
      render();
      save();
      status.textContent = 'Mirrored L → R.';
    },
  });
  mirrorNowBtn.textContent = 'Mirror L → R now';

  const resetBtn = el('button', {
    class: 'button button--ghost',
    type: 'button',
    onclick: () => {
      if (!confirm('Reset the board to the current shipped layout? This discards your edits.'))
        return;
      pushUndo();
      layout = defaultLayout();
      selected = new Set();
      render();
      save();
      updateSelStatus();
      status.textContent = 'Reset to defaults.';
    },
  });
  resetBtn.textContent = 'Reset to defaults';

  const undoBtn = el('button', {
    class: 'button button--ghost',
    type: 'button',
    onclick: () => undo(),
  });
  undoBtn.textContent = 'Undo (Ctrl/Cmd+Z)';

  const copyBtn = el('button', {
    class: 'button',
    type: 'button',
    onclick: async () => {
      try {
        await navigator.clipboard.writeText(jsonBox.value);
        status.textContent = 'Copied JSON to clipboard.';
      } catch {
        jsonBox.select();
        status.textContent = 'Clipboard blocked — text is selected, copy manually (Ctrl/Cmd+C).';
      }
    },
  });
  copyBtn.textContent = 'Copy JSON';

  // --- play test: run the real physics engine against the live layout ---
  let game = null;
  const playScore = el('span', { class: 'pin-ed__playscore' }, '0');
  const playBalls = el('span', { class: 'pin-ed__playballs' });
  const playMsg = el('span', { class: 'pin-ed__status' }, '');
  const playCanvas = el('canvas', { class: 'pin-ed__playcanvas' });
  const playLaunch = el(
    'button',
    { class: 'button pin-ed__playlaunch', type: 'button' },
    'Hold to launch',
  );
  const stopPlayBtn = el(
    'button',
    { class: 'button button--ghost', type: 'button', onclick: () => stopPlayTest() },
    '‹ Back to editing',
  );
  const playWrap = el(
    'div',
    { class: 'pin-ed__playwrap', hidden: true },
    el(
      'div',
      { class: 'pin-ed__toolbar' },
      stopPlayBtn,
      el('span', {}, 'Score ', playScore),
      playBalls,
      playMsg,
    ),
    el('div', { class: 'pin-ed__playboard' }, playCanvas),
    el('div', { class: 'pin-ed__toolbar' }, playLaunch),
    el(
      'p',
      { class: 'pin-ed__hint' },
      '← / → flippers (or tap the table’s left/right half) · hold Space or the button to launch.',
    ),
  );

  const KEYS = { ArrowLeft: 'L', ArrowRight: 'R', a: 'L', d: 'R', z: 'L', l: 'R' };
  function onPlayKey(down) {
    return (e) => {
      if (e.repeat || !game) return;
      const f = KEYS[e.key];
      if (f) {
        game.setFlipper(f, down);
        e.preventDefault();
      } else if (e.key === ' ' || e.key === 'ArrowDown') {
        game.setPlunge(down);
        e.preventDefault();
      }
    };
  }
  const playKeyDown = onPlayKey(true);
  const playKeyUp = onPlayKey(false);
  const pointers = new Map();
  function resyncFlippers() {
    let L = false;
    let R = false;
    for (const side of pointers.values()) side === 'L' ? (L = true) : (R = true);
    game?.setFlipper('L', L);
    game?.setFlipper('R', R);
  }
  const onCanvasDown = (e) => {
    if (!game) return;
    e.preventDefault();
    playCanvas.setPointerCapture?.(e.pointerId);
    const rect = playCanvas.getBoundingClientRect();
    pointers.set(e.pointerId, e.clientX - rect.left < rect.width / 2 ? 'L' : 'R');
    resyncFlippers();
  };
  const onCanvasUp = (e) => {
    if (pointers.delete(e.pointerId)) resyncFlippers();
  };
  const panic = () => {
    game?.setPlunge(false);
    if (pointers.size) {
      pointers.clear();
      resyncFlippers();
    }
  };
  const onLaunchDown = (e) => {
    e.preventDefault();
    game?.setPlunge(true);
  };
  const onLaunchUp = () => game?.setPlunge(false);

  function startPlayTest() {
    if (game) return;
    canvas.hidden = true;
    playWrap.hidden = false;
    playMsg.textContent = '';
    const table = layoutToTable(layout);
    game = createPinball(
      playCanvas,
      {
        onScore: (s) => (playScore.textContent = s.toLocaleString()),
        onBalls: (n) => (playBalls.textContent = '●'.repeat(Math.max(0, n)) || '—'),
        onCharge: (c) => playLaunch.style.setProperty('--charge', c.toFixed(3)),
        onGameOver: (score) => {
          playMsg.textContent = `Game over — ${score.toLocaleString()} pts. Hold to launch to play again.`;
        },
        pickMon: () => ({ id: 25, name: 'test' }), // catches aren't the point of a layout test
      },
      table,
    );
    window.addEventListener('keydown', playKeyDown);
    window.addEventListener('keyup', playKeyUp);
    window.addEventListener('pointerup', onLaunchUp);
    window.addEventListener('pointercancel', panic);
    window.addEventListener('blur', panic);
    playCanvas.addEventListener('pointerdown', onCanvasDown);
    for (const ev of ['pointerup', 'pointercancel', 'pointerleave', 'lostpointercapture'])
      playCanvas.addEventListener(ev, onCanvasUp);
    playLaunch.addEventListener('pointerdown', onLaunchDown);
    for (const ev of ['pointerup', 'pointerleave', 'pointercancel'])
      playLaunch.addEventListener(ev, onLaunchUp);
    requestAnimationFrame(() => game.start());
  }
  function stopPlayTest() {
    if (!game) return;
    game.stop();
    game = null;
    pointers.clear();
    window.removeEventListener('keydown', playKeyDown);
    window.removeEventListener('keyup', playKeyUp);
    window.removeEventListener('pointerup', onLaunchUp);
    window.removeEventListener('pointercancel', panic);
    window.removeEventListener('blur', panic);
    playWrap.hidden = true;
    canvas.hidden = false;
    render();
  }
  const playTestBtn = el(
    'button',
    { class: 'button', type: 'button', onclick: () => startPlayTest() },
    '▶ Play test',
  );

  view.append(
    el('nav', { class: 'lookup__nav' }, el('a', { class: 'link', href: '#/pinball' }, '‹ Pinball')),
    el('header', { class: 'view__header' }, el('h1', {}, 'Pinball board editor')),
    el(
      'p',
      { class: 'pin-ed__hint' },
      'Drag a handle to move just that point. Drag a bare line to move the whole shape. ',
      'Double-click a handle to delete it; double-click a bare line to add a point there. ',
      'The white handle is the plunger — where the ball rests before launch (laneX/laneRestY). ',
      'Drag on empty space to rubber-band select a group of handles (shift-drag or shift-click adds to the selection); ',
      'drag any selected handle to move them all together, or press Delete to remove them (Esc clears the selection). ',
      'Scroll over a bumper or post to resize it. Ctrl/Cmd+Z (or the Undo button) undoes the last change. ',
      '"Play test" runs the real physics against this exact layout, right here, before you export anything. ',
      'Left-side groups (mint outlane, red sling) mirror to the right automatically while "Mirror lock" is on — ',
      'note that multi-select move/delete does NOT auto-mirror; use "Mirror L → R now" after. ',
      'This is a layout tool only — it doesn’t affect the live game until someone copies the JSON back into the code.',
    ),
    el(
      'div',
      { class: 'pin-ed__toolbar' },
      playTestBtn,
      undoBtn,
      mirrorBtn,
      mirrorNowBtn,
      addBumperBtn,
      addPostBtn,
      resetBtn,
      copyBtn,
      status,
      selCount,
    ),
    el('div', { class: 'pin-ed__board' }, canvas),
    playWrap,
    el('h2', { class: 'pin-ed__json-h' }, 'Live JSON (autosaved to this browser)'),
    jsonBox,
  );

  render();
  save();

  onTeardown(view, () => {
    stopPlayTest();
    window.removeEventListener('keydown', onKeyDown);
  });
  return view;
}
